/* eslint-disable camelcase */
import React, { useCallback, useState } from 'react';
import AdminSessionWarningModal from './AdminSessionWarningModal';
import useAdminSessionActions from '~/hooks/useAdminSessionActions';
import useAdminSessionCountdown, { LIVE_INTERVAL_MS } from '~/hooks/useAdminSessionCountdown';
import { clearModalContent } from '~/redux/modules/modalEvent';
import { useAppDispatch } from '~/redux/hooks';

const formatCountdown = (totalSeconds: number) => {
    const safe = Math.max(totalSeconds, 0);
    const minutes = Math.floor(safe / 60);
    const seconds = safe % 60;

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/**
 * The expiry warning, self-contained.
 *
 * It owns its own countdown rather than being handed one, which is the whole
 * point of the split: the watcher dispatches this element into the modal slice
 * exactly once, and the number on screen ticks from here. Otherwise the watcher
 * would re-dispatch a rebuilt element on every change of its own counter, and
 * every step would write JSX into redux.
 *
 * Reads the same deadline from the store as the watcher does, at 1-second
 * resolution, so the two can never disagree about when the session ends.
 *
 * Locking navigates nowhere, unlike the Core web app's version which returns
 * the member to the pinboard. There is no equivalent here — every
 * authenticated route is a staff route, so there is no neutral page to be sent
 * to. Revoking sets the status to 'required' and the gate in PrivateRoute
 * renders over whatever page they were on, which is also where verifying again
 * puts them straight back to work.
 *
 * @returns {JSX.Element} the warning body
 */
export default function AdminSessionWarningModalContainer(): JSX.Element {
    const dispatch = useAppDispatch();

    const { remainingSeconds } = useAdminSessionCountdown(LIVE_INTERVAL_MS);
    const { onRefresh, onRevoke } = useAdminSessionActions();

    // Which action is in flight, not merely that one is. A single boolean was
    // set by both handlers and read only by the Keep working button, so locking
    // put the spinner on the button the member had not pressed.
    const [pendingAction, setPendingAction] = useState<'stay' | 'leave' | null>(null);

    // Closing this rung costs nothing now — the next one still comes, so there
    // is no flag to keep and no state that outlives the modal.
    const onDismiss = useCallback(() => {
        dispatch(clearModalContent());
    }, [dispatch]);

    const onStay = useCallback(async () => {
        setPendingAction('stay');

        try {
            await onRefresh();
            onDismiss();
        } finally {
            setPendingAction(null);
        }
    }, [onDismiss, onRefresh]);

    const onLeave = useCallback(async () => {
        setPendingAction('leave');

        try {
            await onRevoke();
        } finally {
            onDismiss();
            setPendingAction(null);
        }
    }, [onDismiss, onRevoke]);

    return (
        <AdminSessionWarningModal
            countdown={formatCountdown(remainingSeconds)}
            isExtending={pendingAction === 'stay'}
            isLeaving={pendingAction === 'leave'}
            onStay={onStay}
            onLeave={onLeave} />
    );
}
